import { useMemo } from 'react';
import { VARIABLES } from '@app/components/common/constants';
import { ForecastModel } from '@app/common/static-data/models';
import { useTheme } from '@app/theme/ThemeContext';
import { formatChartData } from '../utils/chartUtils';
import { calculateYAxisRange } from '../utils/yAxisUtils';
import { generateEnsembleSeries, generateModelSeries } from '../utils/seriesUtils';
import { getModelColor as getDefaultModelColor } from '../utils/colorUtils';
import { PlottableForecast, VariableId, ModelId } from '../types';

interface UseChartDataParams {
  alignedData: Record<ModelId, PlottableForecast> | null;
  selectedModels: ForecastModel[];
  selectedParam: VariableId;
  showMeanAndConfidence: boolean;
  customModelColors: Record<string, string>;
}

export function useChartData({
  alignedData,
  selectedModels,
  selectedParam,
  showMeanAndConfidence,
  customModelColors,
}: UseChartDataParams) {
  const { theme } = useTheme();

  return useMemo(() => {
    if (!alignedData || !selectedModels.length || !selectedParam) {
      return null;
    }

    const sortedModels = [...selectedModels].sort();
    const variable = VARIABLES.find((v) => v.id === selectedParam);
    if (!variable) return null;

    const chartData = formatChartData(alignedData, sortedModels, variable.juaId);
    const colors = sortedModels.map((modelId, index) =>
      customModelColors[modelId]
        ? customModelColors[modelId]
        : getDefaultModelColor(modelId, index, theme),
    );

    // Extract all values for y-axis range calculation
    const allValues = chartData
      .flatMap((point) =>
        sortedModels.map((modelId) => point[`${variable.juaId}_model_${modelId}`]),
      )
      .filter((val) => val !== undefined && val !== null && !isNaN(val)) as number[];

    const yAxisRange = calculateYAxisRange(allValues);

    // Generate series using extracted utilities
    const { meanSeries, ciSeries } = generateEnsembleSeries(
      chartData,
      sortedModels,
      variable.juaId,
    );

    const modelSeries = generateModelSeries(
      sortedModels,
      chartData,
      colors,
      variable.juaId,
      showMeanAndConfidence,
    );

    const series = [
      ...modelSeries.map((s: any) => ({
        ...s,
        lineStyle: {
          ...s.lineStyle,
          width: 2.5, // Slightly thicker lines for modern look
        },
        symbol: 'none', // Remove point symbols for cleaner look
        smooth: 0.2, // Add slight smoothing
      })),
      ...(showMeanAndConfidence ? meanSeries : []),
      ...(showMeanAndConfidence ? ciSeries : []),
    ];

    return {
      sortedModels,
      variable,
      chartData,
      colors,
      yAxisRange,
      series,
    };
  }, [alignedData, customModelColors, selectedModels, selectedParam, showMeanAndConfidence, theme]);
}
